'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { z } from 'zod'

import { idErrorMessage } from '@package/auth/client'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  FieldSet,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  PhoneInput,
  REGEXP_ONLY_DIGITS,
  toast,
} from '@ui/web/components'
import { formatTimer } from '@helper/utils/format'
import { useLocalStorage } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

import { useAuthClient } from '../context'

const OTP_LENGTH = 6
const RESEND_COOLDOWN_SECONDS = 60

const PhoneNumberSchema = z.object({
  phoneNumber: z
    .union([z.string(), z.null()])
    .refine((val) => val != null && val.replace(/\s/g, '').length > 0, { message: 'Nomor telepon wajib diisi' })
    .refine((val) => val != null && /^\+?[0-9]{10,15}$/.test(val.replace(/\s/g, '')), {
      message: 'Nomor telepon tidak valid',
    }),
})

const OTPSchema = z.object({
  otp: z.string().length(OTP_LENGTH, { message: 'Kode OTP harus 6 digit' }),
})

type Step = 'PHONE_INPUT' | 'OTP_VERIFY'

export function PhoneNumberPopup() {
  const authClient = useAuthClient()
  const { data: sessionData } = authClient.useSession()

  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState<Step>('PHONE_INPUT')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const resendTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [lastPopupDate, setLastPopupDate] = useLocalStorage('phoneNumberPopupLastShown', '')

  useEffect(() => {
    if (!sessionData?.user) return
    if (sessionData.user.phoneNumber) return

    const today = new Date().toISOString().split('T')[0]
    const shouldShowPopup = !lastPopupDate || lastPopupDate !== today

    if (shouldShowPopup) {
      setIsOpen(true)
      setLastPopupDate(today ?? '')
    }
  }, [sessionData, lastPopupDate, setLastPopupDate])

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      resendTimeoutRef.current = setTimeout(() => {
        setResendCooldown((prev) => prev - 1)
      }, 1000)
    }

    return () => {
      if (resendTimeoutRef.current) {
        clearTimeout(resendTimeoutRef.current)
      }
    }
  }, [resendCooldown])

  // --- Phone input form ---
  const phoneForm = useForm({
    defaultValues: {
      phoneNumber: null as string | null,
    },
    validators: {
      onSubmit: PhoneNumberSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const phone = (value.phoneNumber ?? '').replace(/\s/g, '')

      if (!phone) {
        formApi.setErrorMap({
          // @ts-expect-error - TanStack Form errorMap accepts string for onSubmit
          onSubmit: 'Nomor telepon wajib diisi',
        })
        return
      }

      const { error } = await authClient.phoneNumber.sendOtp({
        phoneNumber: phone,
      })

      if (error) {
        formApi.setErrorMap({
          // @ts-expect-error - TanStack Form errorMap accepts string for onSubmit
          onSubmit:
            error.code && idErrorMessage[error.code as keyof typeof idErrorMessage]
              ? idErrorMessage[error.code as keyof typeof idErrorMessage]
              : (error.message ?? 'Gagal mengirim kode OTP'),
        })
        return
      }

      setPhoneNumber(phone)
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
      setStep('OTP_VERIFY')
    },
  })

  // --- OTP verification form ---
  const otpForm = useForm({
    defaultValues: {
      otp: '',
    },
    validators: {
      onSubmit: OTPSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const { error } = await authClient.phoneNumber.verify({
        phoneNumber,
        code: value.otp,
        updatePhoneNumber: true,
      })

      if (error) {
        formApi.setErrorMap({
          // @ts-expect-error - TanStack Form errorMap accepts string for onSubmit
          onSubmit:
            error.code && idErrorMessage[error.code as keyof typeof idErrorMessage]
              ? idErrorMessage[error.code as keyof typeof idErrorMessage]
              : (error.message ?? 'Gagal memverifikasi kode OTP'),
        })
        return
      }

      toast.success('Nomor ponsel berhasil tersimpan')
      setIsOpen(false)
    },
  })

  const handleResendOtp = useCallback(async () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS)

    if (!phoneNumber) return

    const { error } = await authClient.phoneNumber.sendOtp({
      phoneNumber,
    })

    if (error) {
      otpForm.setErrorMap({
        // @ts-expect-error - TanStack Form errorMap accepts string for onSubmit
        onSubmit:
          error.code && idErrorMessage[error.code as keyof typeof idErrorMessage]
            ? idErrorMessage[error.code as keyof typeof idErrorMessage]
            : (error.message ?? 'Gagal mengirim ulang kode OTP'),
      })
    }
  }, [phoneNumber, authClient, otpForm])

  const handleSkip = () => {
    setIsOpen(false)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsOpen(false)
      setStep('PHONE_INPUT')
      phoneForm.reset()
      otpForm.reset()
      setResendCooldown(0)
    }
  }

  // Store selectors
  const phoneErrorMap = useStore(phoneForm.store, (state) => state.errorMap)
  const phoneIsSubmitting = useStore(phoneForm.store, (state) => state.isSubmitting)
  const phoneValues = useStore(phoneForm.store, (state) => state.values)

  const otpErrorMap = useStore(otpForm.store, (state) => state.errorMap)
  const otpIsSubmitting = useStore(otpForm.store, (state) => state.isSubmitting)
  const otpValues = useStore(otpForm.store, (state) => state.values)

  const cleanPhone = (phoneValues.phoneNumber ?? '').replace(/\s/g, '')
  const phoneIsValid = cleanPhone.length > 0 && /^\+?[0-9]{10,15}$/.test(cleanPhone)
  const otpIsValid = OTPSchema.safeParse(otpValues).success

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className='srd:w-full srd:max-w-md'>
        <DialogHeader className='srd:flex srd:flex-col srd:gap-y-2 srd:items-center srd:justify-center srd:text-center'>
          <DialogTitle>
            {step === 'PHONE_INPUT' ? 'Kami ingin dapat terhubung denganmu!' : 'Verifikasi nomor ponsel'}
          </DialogTitle>
          <DialogDescription>
            {step === 'PHONE_INPUT'
              ? 'Bagikan nomor ponselmu untuk membantu kami mempersonalisasi pengalamanmu. Privasimu adalah prioritas kami.'
              : `Masukkan kode verifikasi yang dikirim ke ${phoneNumber}`}
          </DialogDescription>
        </DialogHeader>

        {step === 'PHONE_INPUT' ? (
          <form
            className='srd:flex srd:flex-col srd:gap-y-4 srd:w-full'
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void phoneForm.handleSubmit()
            }}
          >
            <FieldSet>
              <phoneForm.Field name='phoneNumber'>
                {(field) => {
                  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                  const value = field.state.value
                  return (
                    <Field data-invalid={isInvalid} className='srd:w-full'>
                      <FieldLabel htmlFor={field.name}>Nomor ponsel</FieldLabel>
                      <PhoneInput
                        id={field.name}
                        name={field.name}
                        value={value ?? undefined}
                        onBlur={field.handleBlur}
                        onChange={(val) => field.handleChange(val)}
                        aria-invalid={isInvalid}
                      />
                      {isInvalid && <FieldError errors={field.state.meta.errors} />}
                    </Field>
                  )
                }}
              </phoneForm.Field>

              {phoneErrorMap?.onSubmit && typeof phoneErrorMap.onSubmit === 'string' ? (
                <p className='srd:text-sm srd:text-destructive srd:font-medium'>{phoneErrorMap.onSubmit}</p>
              ) : null}

              <Button
                disabled={!phoneIsValid || phoneIsSubmitting}
                loading={phoneIsSubmitting}
                className='srd:rounded-full srd:font-medium srd:bg-main-blue-100 srd:w-full srd:h-fit'
                type='submit'
              >
                Lanjut
              </Button>

              <Button
                variant='link'
                type='button'
                onClick={handleSkip}
                className='srd:text-main-blue-100 srd:text-sm srd:p-0 srd:h-fit'
              >
                Lewati
              </Button>
            </FieldSet>
          </form>
        ) : (
          <form
            className='srd:flex srd:flex-col srd:gap-y-4 srd:w-full'
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void otpForm.handleSubmit()
            }}
          >
            <FieldSet>
              <otpForm.Field name='otp'>
                {(field) => {
                  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
                  return (
                    <Field data-invalid={isInvalid} className='srd:w-full'>
                      <FieldLabel htmlFor={field.name}>Kode OTP</FieldLabel>
                      <div className='srd:flex srd:justify-center'>
                        <InputOTP
                          maxLength={OTP_LENGTH}
                          value={field.state.value}
                          onChange={(value) => field.handleChange(value)}
                          onBlur={field.handleBlur}
                          inputMode='numeric'
                          pattern={REGEXP_ONLY_DIGITS}
                        >
                          <InputOTPGroup>
                            {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                              <InputOTPSlot key={i} className='srd:bg-white srd:size-12 srd:text-xl' index={i} />
                            ))}
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                      {isInvalid && <FieldError errors={field.state.meta.errors} />}
                    </Field>
                  )
                }}
              </otpForm.Field>

              <section className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-1 srd:text-center'>
                <h6 className='srd:text-sm srd:font-medium'>Tidak menerima kode verifikasi?</h6>
                <p className='srd:text-sm'>
                  Kirim ulang{' '}
                  <Button
                    variant='link'
                    type='button'
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0}
                    className={cn(resendCooldown === 0 && 'srd:text-main-blue-100', 'srd:p-0 srd:text-sm')}
                  >
                    {resendCooldown > 0 ? `dalam ${formatTimer(resendCooldown)}` : 'sekarang'}
                  </Button>
                </p>
              </section>

              {otpErrorMap?.onSubmit && typeof otpErrorMap.onSubmit === 'string' ? (
                <p className='srd:text-sm srd:text-destructive srd:font-medium'>{otpErrorMap.onSubmit}</p>
              ) : null}

              <Button
                disabled={!otpIsValid || otpIsSubmitting}
                loading={otpIsSubmitting}
                className='srd:rounded-full srd:font-medium srd:bg-main-blue-100 srd:w-full srd:h-fit'
                type='submit'
              >
                Verifikasi
              </Button>

              <div className='srd:flex srd:flex-col srd:items-center srd:gap-y-2'>
                <Button
                  variant='link'
                  type='button'
                  onClick={handleSkip}
                  className='srd:text-main-blue-100 srd:text-sm srd:p-0 srd:h-fit'
                >
                  Lewati
                </Button>
                <Button
                  variant='link'
                  type='button'
                  disabled={otpIsSubmitting}
                  onClick={() => {
                    setStep('PHONE_INPUT')
                    otpForm.reset()
                    setResendCooldown(0)
                  }}
                  className='srd:text-main-blue-100 srd:text-xs srd:p-0 srd:h-fit'
                >
                  Ubah nomor telepon
                </Button>
              </div>
            </FieldSet>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
